/**
 * Handle the behaviors of the post list block
 */
const PostList = {
	/**
	 * Keep track of the num of pages during infinite scroll
	 */
	infiniteScrollPage: 1,
	/**
	 * Entry point
	 */
	_init: () => {
		// listen for document ready
		document.addEventListener("DOMContentLoaded", PostList._ready);
	},
	/**
	 * Fired when document ready
	 */
	_ready: () => {
		// listen for UI interactions
		PostList._addEventListeners();
	},
	/**
	 * Binds event listeners & listens for UI interactions
	 */
	_addEventListeners: () => {
		// listen for search input changes
		const searchInput = document.querySelector(
			".postlist-wrapper-form-label-input",
		);
		if (searchInput instanceof HTMLElement) {
			searchInput.addEventListener("input", PostList._searchInputChangeHandler);
		}

		// listen for search form submit
		const searchForm = document.querySelector(".postlist-wrapper-form");
		if (searchForm instanceof HTMLElement) {
			searchForm.addEventListener("submit", PostList._searchFormSubmitHandler);
		}

		// listen for buttons checking on view toggle
		const viewButtons = document.querySelectorAll(
			".postlist-wrapper-header-viewtoggle-btn",
		);
		viewButtons.forEach((btn) => {
			if (btn instanceof HTMLElement) {
				btn.addEventListener("click", PostList._viewButtonClickHandler);
			}
		});

		// listen for pill clicking
		const pills = document.querySelectorAll(
			".postlist-wrapper-pills-row-pill-link",
		);
		if (pills.length > 0) {
			pills.forEach((pill) => {
				pill.addEventListener("click", PostList._pillClickHandler);
			});
		}

		// listen for search pagination link clicks
		const searchPaginationLinks = document.querySelectorAll(
			'.postlist[data-list_option="search"] .postlist-wrapper-pagination a.page-numbers',
		);
		if (searchPaginationLinks.length > 0) {
			searchPaginationLinks.forEach((paginationLink) => {
				paginationLink.addEventListener(
					"click",
					PostList._searchPaginationClickHandler,
				);
			});
		}

		// listen for not search pagination link clicks
		const paginationLinks = document.querySelectorAll(
			'.postlist[data-list_option]:not([data-list_option="search"]) .postlist-wrapper-pagination a.page-numbers',
		);
		if (paginationLinks.length > 0) {
			paginationLinks.forEach((paginationLink) => {
				paginationLink.addEventListener(
					"click",
					PostList._paginationClickHandler,
				);
			});
		}

		// listen for scroll to bottom
		window.addEventListener(
			"scroll",
			PostList._debounceLeading(PostList._scrollHandler),
		);

		// listen for popstate (for back button)
		window.addEventListener("popstate", PostList._popstateHandler);
	},
	/**
	 * Handle the icon change on search input change
	 * @param {object} e the event object
	 */
	_searchInputChangeHandler: (e) => {
		const searchIcon = document.querySelector(
			".postlist-wrapper-form-submit-icon.search",
		);
		const submitIcon = document.querySelector(
			".postlist-wrapper-form-submit-icon.submit",
		);
		if (e.target.value.length > 0) {
			searchIcon.classList.remove("active");
			submitIcon.classList.add("active");
		} else {
			submitIcon.classList.remove("active");
			searchIcon.classList.add("active");
		}
	},
	/**
	 * Handle the search submit event.
	 * @param {object} e the event object
	 */
	_searchFormSubmitHandler: (e) => {
		// don't do the not-ajax submit
		e.preventDefault();

		// update the URL & history
		const sq = document.querySelector(
			".postlist-wrapper-form-label-input",
		).value;
		const url = new URL(window.location);
		url.searchParams.set("sq", sq);

		history.pushState({}, "", url);

		// query for results
		PostList._searchFilterClickHandler();
	},
	/**
	 * Handle clicking the view buttons
	 * @param {object} e the event object
	 */
	_viewButtonClickHandler: (e) => {
		const btn = e.currentTarget;

		const listBtn = document.querySelector(
			".postlist-wrapper-header-viewtoggle-btn.list",
		);
		const gridBtn = document.querySelector(
			".postlist-wrapper-header-viewtoggle-btn.grid",
		);

		let view = "";

		if (btn.classList.contains("grid")) {
			view = "grid";
			btn.classList.add("active");
			listBtn.classList.remove("active");
		} else if (btn.classList.contains("list")) {
			view = "list";
			btn.classList.add("active");
			gridBtn.classList.remove("active");
		}

		// update the URL & history
		const url = new URL(window.location);
		url.searchParams.set("view", view);

		history.pushState({}, "", url);

		// query for results
		PostList._searchFilterClickHandler();
	},
	/**
	 * Handle clicking the pills.
	 * @param {object} e the event object
	 */
	_pillClickHandler: (e) => {
		// prevent # in url.
		e.preventDefault();
		// build the new URL
		const url = new URL(window.location);
		url.searchParams.set(
			e.target.dataset.queryparam,
			e.target.dataset.queryvalue,
		);

		// if we're clicking on the top pills and it's not artwork, clear the art_class param
		if (
			"search_post_type" === e.target.dataset.queryparam &&
			"artwork" !== e.target.dataset.queryvalue
		) {
			url.searchParams.delete("art_class");
		}

		// reset pagination
		url.searchParams.delete("page_num");

		// set the new url
		history.pushState({}, "", url);

		// query for results
		PostList._searchFilterClickHandler();
	},
	/**
	 * Handle pagination click
	 * @param {object} e the event object
	 */
	_searchPaginationClickHandler: (e) => {
		// prevent non-ajax way
		e.preventDefault();

		// get the page num from the target
		const pageNum = e.target.innerHTML;

		// update the URL & history
		const url = new URL(window.location);
		url.searchParams.set("page_num", pageNum);

		history.pushState({}, "", url);

		// scroll to the top of the post list
		const postList = document.querySelector(".postlist-wrapper");
		postList.scrollIntoView({
			behavior: "smooth",
		});

		// query for results
		PostList._searchFilterClickHandler();
	},
	_paginationClickHandler: (e) => {
		// prevent reloading of page
		e.preventDefault();
		const postList = document.querySelector(".postlist");
		const headline = e.target
			.closest(".postlist")
			.querySelector(".postlist-wrapper-headline");

		// Update values in the URL on artwork list pagination
		let currenturl = new URL(window.location);
		const view = currenturl.searchParams.get("view") ?? "";
		const artClass = currenturl.searchParams.get("art_class") ?? "all";

		PostList._searchFilterClickHandler(
			false,
			{
				page_num: e.target.innerHTML,
				headline: headline ? headline.textContent : "",
				past_exhibitions_only: postList.dataset.pastExhibitionsOnly,
				anchor: postList.id,
				view: view,
				art_class: artClass,
			},
			e,
		);

		const rootStyles = getComputedStyle(document.documentElement);
		const headerBarHeight = parseFloat(
			rootStyles.getPropertyValue("--header-height-expanded").trim(),
		);
		console.log(e.target.closest(".postlist").offsetTop);
		// scroll to the top of the post list
		window.scrollTo({
			top: e.target.closest(".postlist").offsetTop - headerBarHeight,
			behavior: "smooth",
		});
	},
	/**
	 * Handle infinite scrolling
	 * @param {object} e the event object
	 */
	_scrollHandler: () => {
		// if we're using the grid view
		if (
			document.querySelector(".postlist-wrapper-list.grid") instanceof
			HTMLElement
		) {
			// if we've reached the bottom && there's more posts to get
			const atTheBottom =
				window.innerHeight + window.scrollY >
				document.querySelector(".postlist-wrapper-list").getBoundingClientRect()
					.bottom +
					window.scrollY;
			const morePostsToGet =
				document.querySelectorAll(".postlist-wrapper-list-item").length <
				document.querySelector(".postlist").dataset.foundposts;

			if (atTheBottom && morePostsToGet) {
				// console.log('below the fold')
				// we're paginating
				PostList._searchFilterClickHandler(true);
			}
		}
	},
	/**
	 *
	 * @param {CallableFunction} func the fuction to
	 * @param {Integer} timeout timeout in ms
	 * @returns callable
	 */
	_debounceLeading(func, timeout = 300) {
		let timer;
		return (...args) => {
			if (!timer) {
				func.apply(this, args);
			}
			clearTimeout(timer);
			timer = setTimeout(() => {
				timer = undefined;
			}, timeout);
		};
	},
	/**
	 * When back button presses, re-render.
	 * @param {object} e the event object
	 */
	_popstateHandler: (e) => {
		// query for results
		PostList._searchFilterClickHandler();
	},
	/**
	 * Fired when the search filters have been clicked/modified
	 */
	_searchFilterClickHandler: async (
		paginating = false,
		urlParams = {},
		clickEvent = {},
	) => {
		// refresh the events list via the api
		try {
			console.log(urlParams);
			// map all current search params to the fetch url
			let fetchurl = new URL(ajaxurl);
			// if passing in optional urlParams, use those instead of the current URL for params
			if (Object.keys(urlParams).length) {
				for (const urlParam in urlParams) {
					fetchurl.searchParams.append(urlParam, urlParams[urlParam]);
				}
			} else {
				let currenturl = new URL(window.location);
				currenturl.searchParams.forEach((value, key) => {
					fetchurl.searchParams.append(key, value);
				});
			}

			// if click event is set it means there's likely multiple instances of this block in the post_content
			if (Object.keys(clickEvent).length === 0) {
				// add list option context for search page.
				const listOption =
					document.querySelector(".postlist").dataset.list_option;
				fetchurl.searchParams.set("list_option", listOption);
			} else {
				if (clickEvent.target.closest(".postlist").dataset.list_ids.length) {
					fetchurl.searchParams.set(
						"list_items",
						clickEvent.target.closest(".postlist").dataset.list_ids,
					);
				}
				fetchurl.searchParams.set(
					"list_option",
					clickEvent.target.closest(".postlist").dataset.list_option,
				);
			}

			// if we're paginating let the BE know too.
			if (paginating) {
				fetchurl.searchParams.set("paginating", true);
			}
			// if we're paginating on the search page
			if (paginating && Object.keys(clickEvent).length === 0) {
				PostList.infiniteScrollPage++;
				fetchurl.searchParams.set("page_num", PostList.infiniteScrollPage);
			}

			// build the form data to send to the BE
			var formData = new FormData();
			formData.append("action", "refresh_search_list");

			// use async/await fetch to get the response
			const res = await fetch(fetchurl, {
				method: "POST",
				body: formData,
			});
			const response_text = await res.text();

			// render the response
			if (paginating) {
				if (Object.keys(clickEvent).length) {
					clickEvent.target
						.closest(".postlist")
						.querySelector(".postlist-wrapper-list")
						.insertAdjacentHTML("beforeend", response_text);
				} else {
					document
						.querySelector(".postlist-wrapper-list")
						.insertAdjacentHTML("beforeend", response_text);
				}
			} else {
				if (Object.keys(clickEvent).length) {
					clickEvent.target.closest(".postlist").outerHTML = response_text;
				} else {
					document.querySelector(".postlist").outerHTML = response_text;
				}
			}

			// rebind event listeners
			PostList._addEventListeners();
			document.dispatchEvent(new Event("list:updated"));
		} catch (error) {
			console.log(error);
		}
	},
};

// let's gooo.
PostList._init();
